"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MessageSquare, MessageCircle } from "lucide-react";
import HeroVideo from "./HeroVideo";
import HeroBadge from "./HeroBadge";
import HeroContent from "./HeroContent";
import HeroScrollIndicator from "./HeroScrollIndicator";

export const Hero: React.FC = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const { innerWidth, innerHeight } = window;
      setMousePosition({
        x: (e.clientX / innerWidth - 0.5) * 10,
        y: (e.clientY / innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Calm Stagger Container Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.18,
        delayChildren: 0.15,
      },
    },
  };

  // Fade-in-up variants with calm Apple-like cubic bezier easing (1.0s duration)
  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.0,
        ease: [0.16, 1, 0.3, 1] as const,
      },
    },
  };

  const contactActions = [
    {
      icon: <Mail className="w-5 h-5" />,
      link: "mailto:info@shivalikcollege.edu.in",
      title: "Email Us",
      color: "hover:bg-[#D72638] hover:text-white hover:border-[#D72638] hover:shadow-[#D72638]/30",
    },
    {
      icon: <Phone className="w-5 h-5" />,
      link: "tel:+919997155111",
      title: "Call Us",
      color: "hover:bg-[#FF6B35] hover:text-white hover:border-[#FF6B35] hover:shadow-[#FF6B35]/30",
    },
    {
      icon: <MessageCircle className="w-5 h-5" />,
      link: "https://wa.me/919997155111",
      title: "WhatsApp",
      color: "hover:bg-emerald-500 hover:text-white hover:border-emerald-500 hover:shadow-emerald-500/30",
    },
  ];

  return (
    <section className="relative min-h-screen lg:h-screen lg:min-h-0 w-full overflow-hidden flex flex-col justify-between bg-[#050B14]">
      {/* Cinematic Background Video */}
      <HeroVideo />

      {/* Very Soft Ambient Glow Orbs */}
      <motion.div
        animate={{ x: mousePosition.x, y: mousePosition.y }}
        transition={{ type: "spring", damping: 30, stiffness: 100 }}
        className="absolute inset-0 pointer-events-none z-[3] opacity-10"
      >
        <div className="absolute top-1/4 left-1/5 w-80 h-80 rounded-full bg-[#D72638]/20 blur-3xl" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 rounded-full bg-[#D72638]/10 blur-3xl" />
      </motion.div>

      {/* Content Wrapper */}
      <div className="relative z-10 flex-grow flex flex-col items-center justify-center pt-32 pb-24 lg:py-0 px-4 sm:px-6 md:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full flex flex-col items-center"
        >
          {/* 1. Admissions Glass Badge */}
          <motion.div variants={itemVariants} className="mb-8 sm:mb-9">
            <HeroBadge />
          </motion.div>

          {/* 2. Heading, Tagline & Description */}
          <HeroContent itemVariants={itemVariants} />
        </motion.div>
      </div>

      {/* 3. Primary Interactive Scroll Indicator */}
      <HeroScrollIndicator />

      {/* Fixed Sticky Action Buttons (Right edge) */}
      <div className="fixed right-4 sm:right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-3.5">
        {contactActions.map((item, idx) => (
          <motion.a
            key={idx}
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            title={item.title}
            whileHover={{ scale: 1.1, x: -3 }}
            whileTap={{ scale: 0.95 }}
            className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white/95 bg-black/40 backdrop-blur-md border border-white/10 shadow-lg cursor-pointer transition-all duration-300 ${item.color}`}
          >
            {item.icon}
          </motion.a>
        ))}
      </div>

      {/* Fixed Chatbot Floating Button (Bottom right) */}
      <motion.button
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => {
          console.log("Chatbot clicked");
        }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center text-white bg-gradient-to-tr from-[#D72638] to-[#B5121B] border border-white/15 shadow-[0_8px_30px_rgba(215,38,56,0.4)] hover:shadow-[0_12px_35px_rgba(215,38,56,0.6)] cursor-pointer transition-all duration-300"
        aria-label="Open chat assistant"
      >
        <MessageSquare className="w-6 h-6" />
      </motion.button>
    </section>
  );
};

export default Hero;
